import { Link } from "react-router-dom";
import {
  ArrowRight, Flame, Mountain, SunMedium, Cpu, ShieldCheck, Wrench,
  Camera, MonitorSmartphone, Smartphone, Armchair, Volume2, DoorOpen, Anchor, Sparkles, Gauge,
} from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import HotspotView from "@/components/HotspotView";

const VIEWS = [
  { img: "/images/suv-forest.jpg", label: "FRONT VIEW" },
  { img: "/images/suv-rear.jpg", label: "REAR VIEW" },
  { img: "/images/suv-side.jpg", label: "SIDE VIEW" },
  { img: "/images/suv-offroad.jpg", label: "OFF-ROAD MODE" },
  { img: "/images/suv-city.jpg", label: "CITY MODE" },
];

const USPS = [
  { icon: Sparkles, title: "Bold Design" },
  { icon: Cpu, title: "Advanced Technology" },
  { icon: Gauge, title: "Powerful Performance" },
  { icon: SunMedium, title: "Luxury Comfort" },
  { icon: ShieldCheck, title: "Maximum Safety" },
  { icon: Mountain, title: "Made for Every Terrain" },
];

const FEATURES = [
  { icon: Flame, value: "2.0L / 3.0L", label: "Diesel Engine (200-500 HP)" },
  { icon: Mountain, value: "4x4 AWD", label: "with Terrain Modes" },
  { icon: Cpu, value: "Air Suspension", label: "Adjustable Height" },
  { icon: Camera, value: "360° Camera", label: "with Off-Road View" },
  { icon: MonitorSmartphone, value: "12.3\" + 15\"", label: "Dual Display" },
  { icon: Smartphone, value: "Wireless", label: "Apple CarPlay & Android Auto" },
  { icon: ShieldCheck, value: "ADAS", label: "Advanced Driver Assistance" },
  { icon: Armchair, value: "Ventilated &", label: "Heated Seats" },
  { icon: Volume2, value: "Premium", label: "Sound System" },
  { icon: DoorOpen, value: "Power", label: "Tailgate" },
  { icon: Anchor, value: "3,500 kg", label: "Towing Capacity" },
  { icon: Wrench, value: "₹300", label: "Lifetime Servicing" },
];

const COLORS = [
  { name: "Forest Green", swatch: "#5e6b4a", img: "/images/suv-forest.jpg" },
  { name: "Midnight Black", swatch: "#0f0f14", img: "/images/suv-midnight.jpg" },
  { name: "Glacier White", swatch: "#e8e9ed", img: "/images/suv-glacier.jpg" },
  { name: "Desert Sand", swatch: "#c2a878", img: "/images/suv-sand.jpg" },
  { name: "Steel Grey", swatch: "#8b9096", img: "/images/suv-steel.jpg" },
  { name: "Matte Black", swatch: "#1c1c1e", img: "/images/suv-matte.jpg" },
];

const VARIANTS = [
  { id: "EXPLORER", price: "₹50 Lakh", bullets: ["Essential Features", "4x4 AWD", "Premium Interior"], img: "/images/suv-forest.jpg" },
  { id: "ADVENTURE", price: "₹55 Lakh", bullets: ["Advanced Off-Road Pack", "Air Suspension", "Panoramic Sunroof"], img: "/images/suv-offroad.jpg" },
  { id: "SIGNATURE", price: "₹60 Lakh", bullets: ["Full Luxury Features", "ADAS", "Premium Sound System"], img: "/images/suv-matte.jpg" },
];

function Hero() {
  return (
    <section className="relative overflow-hidden pt-36 pb-16" data-testid="suv-hero">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse, rgba(230,184,0,0.14), transparent 62%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow">IshuCar SUV · Off-Road 4x4 AWD · Diesel</p>
          <h1 className="mt-4 font-display text-[clamp(1.6rem,6vw,3rem)] font-extrabold uppercase leading-[1.08] tracking-tight text-white">
            Power <span className="text-gold-gradient">meets</span> purpose
          </h1>
          <p className="mt-5 max-w-lg text-base text-neutral-400 sm:text-lg">
            200 से 500 HP तक का diesel दम, 4x4 AWD और Air Suspension — पहाड़ हो, रेगिस्तान हो या शहर,
            IshuCar SUV हर terrain पर राज करती है।
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500" data-testid="suv-tags">
            <span>Adventure</span><span className="text-[#e6b800]">|</span>
            <span>Family</span><span className="text-[#e6b800]">|</span>
            <span>Business</span><span className="text-[#e6b800]">|</span>
            <span>Everyday</span>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/booking/suv"
              data-testid="suv-reserve-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-8 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
            >
              Reserve for ₹10,000
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <div className="text-left" data-testid="suv-price-line">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">Starting at</p>
              <p className="font-display text-2xl font-extrabold text-[#e6b800]">₹50 Lakh*</p>
              <p className="text-[10px] text-neutral-500">(Ex-showroom)</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img src="/images/suv-forest.jpg" alt="IshuCar SUV — Power Meets Purpose" className="h-full w-full object-cover" />
          </div>
          <p className="mt-4 text-right font-mono2 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
            IshuCar SUV · Forest Green
          </p>
        </Reveal>
      </div>
      <div className="relative mx-auto mt-12 max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="suv-usp-strip">
          {USPS.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.05}>
              <div className="flex h-full items-center gap-2.5 rounded-xl border border-white/10 bg-[#111116] px-4 py-3 transition-colors duration-300 hover:border-[#e6b800]/50">
                <u.icon size={16} className="shrink-0 text-[#e6b800]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white">{u.title}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ViewsGallery() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="suv-views">
      <SectionHeading
        eyebrow="Every angle · हर नज़रिया"
        title="एक ही SUV, पाँच अंदाज़"
        sub="शहर की चमक से लेकर रेगिस्तान की धूल तक — हर मोड पर अपना ही लुक।"
      />
      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {VIEWS.map((v, i) => (
          <Reveal key={v.label} delay={i * 0.06} className={i === 0 ? "sm:col-span-2 lg:col-span-1 lg:row-span-2" : ""}>
            <div className="group relative h-full overflow-hidden rounded-2xl border border-white/10" data-testid={`suv-view-${i}`}>
              <img
                src={v.img}
                alt={`IshuCar SUV — ${v.label}`}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "h-72 lg:h-full" : "h-52"}`}
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#070709]/90 to-transparent px-4 pb-3 pt-10">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/90">{v.label}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal delay={0.3} className="sm:col-span-2">
          <div className="group relative h-full overflow-hidden rounded-2xl border border-white/10" data-testid="suv-interior-dashboard">
            <img src="/images/suv-dashboard.jpg" alt="IshuCar SUV — interior dashboard" loading="lazy" className="h-52 w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#070709]/90 to-transparent px-4 pb-3 pt-10">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/90">CABIN · DUAL DISPLAY</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function SuvHotspotSection() {
  const SUV_HOTSPOTS = [
    { id: "suv-engine", x: 52, y: 40, tag: "Power", title: "2.0L / 3.0L Diesel · 200-500 HP", desc: "हल्की से भारी तक — दोनों engines, high torque और automatic gearbox के साथ।" },
    { id: "suv-led", x: 63, y: 45, tag: "Light", title: "Round-Square LED Headlights", desc: "Signature DRLs — दिन में भी दमदार, रात में आइकनिक।" },
    { id: "suv-roof", x: 50, y: 16, tag: "Roof", title: "Roof Rack + LED Light Bar", desc: "एडवेंचर के लिए तैयार — छत पर luggage, रास्ते में रौशनी।" },
    { id: "suv-wheel", x: 30, y: 68, tag: "Grip", title: "4x4 AWD + Terrain Modes", desc: "Sand, Snow, Rock, Mud — हर terrain का अपना mode, हर पहिये की पकड़।" },
    { id: "suv-suspension", x: 60, y: 62, tag: "Comfort", title: "Air Suspension — Adjustable Height", desc: "रास्ते के हिसाब से ऊँचाई — शहर में नरम, off-road पर ऊँची।" },
  ];
  return (
    <section className="border-y border-white/10 bg-[#0a0a0e] py-24 lg:py-32" data-testid="hotspot-section-suv">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow text-center">Touch to reveal the invisible</p>
          <h2 className="mt-4 text-center font-display text-3xl font-extrabold uppercase text-white sm:text-5xl">
            SUV के <span className="text-gold-gradient">राज़, छू कर देखिए</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-sm text-neutral-400 sm:text-base">
            Car पर चमकते points touch करीए — हर point के पीछे एक कहानी है।
          </p>
        </Reveal>
        <Reveal className="mt-12">
          <HotspotView
            image="/images/suv-forest.jpg"
            alt="IshuCar SUV — touch to reveal"
            hotspots={SUV_HOTSPOTS}
            eyebrow="Car पर points touch करें"
            testid="hotspot-view-suv"
          />
        </Reveal>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="suv-features">
      <SectionHeading
        eyebrow="Key features & functions"
        title="सब कुछ, जो एक बड़ी SUV में चाहिए"
        sub="Technology, comfort और safety — बिना किसी समझौते के।"
      />
      <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {FEATURES.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.04}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#111116] p-5 text-center transition-colors duration-300 hover:border-[#e6b800]/50" data-testid={`suv-feature-${i}`}>
              <s.icon size={20} className="mx-auto text-[#e6b800]" />
              <p className="mt-3 font-display text-base font-extrabold text-white sm:text-lg">{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ColoursVariants() {
  return (
    <section className="border-t border-white/10 bg-[#0a0a0e] py-24 lg:py-28" data-testid="suv-colours-variants">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Colours & variants"
          title="आपका SUV, आपका रंग"
          sub="6 देशी-विदेशी रंग और 3 variants — Explorer से Signature तक।"
        />
        <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="suv-colours">
          {COLORS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.05}>
              <div className="group overflow-hidden rounded-2xl border border-white/10 bg-[#111116] transition-colors duration-300 hover:border-[#e6b800]/50" data-testid={`suv-colour-${i}`}>
                <img src={c.img} alt={`IshuCar SUV — ${c.name}`} loading="lazy" className="h-28 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="flex items-center gap-2 px-3 py-2.5">
                  <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-white/30" style={{ background: c.swatch }} />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white">{c.name}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-3" data-testid="suv-variants">
          {VARIANTS.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.08}>
              <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111116] transition-colors duration-300 hover:border-[#e6b800]/50" data-testid={`suv-variant-${v.id.toLowerCase()}`}>
                <div className="relative">
                  <img src={v.img} alt={`IshuCar SUV ${v.id}`} loading="lazy" className="h-40 w-full object-cover" />
                  <span className="absolute left-4 top-4 rounded-full bg-[#e6b800] px-2.5 py-1 font-mono2 text-[9px] font-bold uppercase tracking-[0.18em] text-black">
                    {v.id}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-display text-3xl font-extrabold text-white">
                    {v.price}<span className="ml-1 text-xs font-semibold text-neutral-500">(Ex-Showroom)</span>
                  </p>
                  <ul className="mt-4 space-y-2">
                    {v.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-neutral-300">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#e6b800]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 text-center">
          <Link
            to="/booking/suv"
            data-testid="suv-bottom-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-9 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
          >
            Reserve your SUV — ₹10,000
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <p className="mt-4 text-[11px] text-neutral-600">
            *Ex-showroom price, indicative — insurance, RTO and on-road charges extra.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function LuxurySection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="suv-luxury">
      <SectionHeading
        eyebrow="Luxury comfort · आराम"
        title="अंदर की दुनिया"
        sub="Panoramic sunroof और quilted leather seats — हर सवारी को ख़ास बनाने के लिए।"
      />
      <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { img: "/images/suv-sunroof.jpg", label: "PANORAMIC SUNROOF" },
          { img: "/images/suv-seats.jpg", label: "PREMIUM LEATHER SEATS" },
          { img: "/images/suv-dashboard.jpg", label: "DUAL DISPLAY COCKPIT" },
        ].map((x, i) => (
          <Reveal key={x.label} delay={i * 0.08}>
            <div className="group relative overflow-hidden rounded-2xl border border-white/10" data-testid={`suv-luxury-${i}`}>
              <img src={x.img} alt={`IshuCar SUV — ${x.label}`} loading="lazy" className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#070709]/90 to-transparent px-4 pb-3 pt-10">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/90">{x.label}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Suv() {
  return (
    <div data-testid="suv-page">
      <Hero />
      <ViewsGallery />
      <SuvHotspotSection />
      <FeaturesSection />
      <LuxurySection />
      <ColoursVariants />
    </div>
  );
}
